import * as React from 'react';
import { messages } from './messages';

export type Locale = 'ru' | 'en';
type Vars = Record<string, string | number>;

const LocaleCtx = React.createContext<{ locale: Locale; setLocale: (l: Locale) => void }>({ locale: 'ru', setLocale: () => {} });

function readStored(): Locale {
  try {
    const v = localStorage.getItem('gn.locale');
    return v === 'en' ? 'en' : 'ru';
  } catch {
    return 'ru';
  }
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(readStored);
  const setLocale = React.useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem('gn.locale', l);
    } catch {
      /* private mode */
    }
    document.documentElement.lang = l;
  }, []);
  React.useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return <LocaleCtx.Provider value={{ locale, setLocale }}>{children}</LocaleCtx.Provider>;
}

export function translate(locale: Locale, key: string, vars?: Vars): string {
  const raw = messages[locale][key] ?? messages.ru[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

/** UI copy is externalised (spec §4.1): `t('ws.submit')`. Greek study content is never translated. */
export function useT() {
  const { locale } = React.useContext(LocaleCtx);
  return React.useCallback((key: string, vars?: Vars) => translate(locale, key, vars), [locale]);
}

export function useLocale() {
  return React.useContext(LocaleCtx);
}

export function plural(locale: Locale, n: number, forms: { one: string; few?: string; many: string }): string {
  if (locale === 'en') return n === 1 ? forms.one : forms.many;
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms.one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return forms.few ?? forms.many;
  return forms.many;
}

export function formatDate(locale: Locale, iso: string | null | undefined, withTime = true): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : { year: 'numeric' }),
  });
}
